// import axios, { AxiosError } from 'axios'
import axios from 'axios'
import { defaultRequestInterceptors, defaultResponseInterceptors } from './config'

import { AxiosInstance, InternalAxiosRequestConfig, RequestConfig, AxiosResponse } from './types'
import { ElMessage } from 'element-plus'
import { REQUEST_TIMEOUT } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'

export const PATH_URL = import.meta.env.VITE_API_BASE_PATH
/*
单点登录
 */
// ===== 刷新配置（按你后端改路径/数据格式）=====
const REFRESH_URL = '/ors-auth/oauth2/token' // 你们后端的刷新接口路径
const isRefreshRequest = (config?: InternalAxiosRequestConfig) =>
  !!config?.url && config.url.includes(REFRESH_URL)

const buildRefreshBody = (refreshToken: string) =>
  new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken
  })
// =============================================

// 并发 401 合并
let isRefreshing = false
let pendingQueue: Array<(token: string) => void> = []
function onRefreshed(token: string) {
  pendingQueue.forEach((cb) => cb(token))
  pendingQueue = []
}
function enqueue(cb: (token: string) => void) {
  pendingQueue.push(cb)
}

async function refreshAccessToken(): Promise<string> {
  const userStore = useUserStoreWithOut()
  const refreshToken = userStore.getRefreshToken || userStore.refreshToken

  if (!refreshToken) throw new Error('No refresh token')

  // 用 axios 原始请求，避免你实例拦截器再次套娃
  const resp = await axios.request({
    baseURL: PATH_URL,
    url: REFRESH_URL,
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    data: buildRefreshBody(refreshToken),
    timeout: REQUEST_TIMEOUT
  })

  const data = resp.data
  const newAccess = data?.token || data?.accessToken || data?.data?.accessToken || data?.data?.token
  const newRefresh =
    data?.refresh_token ||
    data?.refreshToken ||
    data?.data?.refreshToken ||
    data?.data?.refresh_token

  if (!newAccess) throw new Error('Refresh missing token')

  // 回写 store（你有 actions.setToken / setRefreshToken）
  userStore.setToken?.(newAccess) ?? (userStore.token = newAccess)
  if (newRefresh) userStore.setRefreshToken?.(newRefresh) ?? (userStore.refreshToken = newRefresh)

  return newAccess
}
/****************/

const abortControllerMap: Map<string, AbortController> = new Map()

const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL
})

axiosInstance.interceptors.request.use((res: InternalAxiosRequestConfig) => {
  const controller = new AbortController()
  const url = res.url || ''
  res.signal = controller.signal
  abortControllerMap.set(
    import.meta.env.VITE_USE_MOCK === 'false' ? url.replace('/mock', '') : url,
    controller
  )
  return res
})

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const url = res.config.url || ''
    abortControllerMap.delete(url)
    // 这里不能做任何处理，否则后面的 interceptors 拿不到完整的上下文了
    return res
  },
  async (error: any) => {
    // debugger
    const response = error.response
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (!response) {
      // 网络错误/超时等
      // ElMessage.error(error.msg)
      return Promise.reject(error)
    }
    // debugger
    // 刷新接口自己 401/403：静默登出
    if (isRefreshRequest(original)) {
      try {
        useUserStoreWithOut().logout()
      } catch (err) {
        console.log(err)
      }
      return Promise.reject(error)
    }

    // 只有 401 才考虑刷新
    if (response.status === 401) {
      const userStore = useUserStoreWithOut()

      // 防止自旋
      if (original._retry) {
        userStore.logout()
        return Promise.reject(error)
      }

      const hasRefresh = userStore.getRefreshToken || userStore.refreshToken
      if (!hasRefresh) {
        userStore.logout()
        return Promise.reject(error)
      }

      // 有刷新 token：并发合并
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          enqueue((newToken: string) => {
            try {
              original._retry = true
              original.headers = original.headers || {}
              ;(original.headers as any)[userStore.getTokenKey || 'Authorization'] =
                `Bearer ${newToken}`
              resolve(axiosInstance(original))
            } catch (e) {
              reject(e)
            }
          })
        })
      }

      // 发起刷新
      isRefreshing = true
      original._retry = true
      try {
        const newToken = await refreshAccessToken()
        onRefreshed(newToken)
        original.headers = original.headers || {}
        ;(original.headers as any)['Authorization'] = `Bearer ${newToken}`
        return axiosInstance(original)
      } catch (e) {
        userStore.logout()
        return Promise.reject(e)
      } finally {
        isRefreshing = false
      }
    } else {
      // 非 401：沿用你原来的提示
      ElMessage.error(error.response.data.msg)
    }
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.request.use(defaultRequestInterceptors)
axiosInstance.interceptors.response.use(defaultResponseInterceptors)

const service = {
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any)
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res)
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  },
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url]
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort()
      abortControllerMap.delete(_url)
    }
  },
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort()
    }
    abortControllerMap.clear()
  }
}

export default service
